import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { MemberCardStoryblok, StoryblokMultilink } from '../../types/storyblok';
import { ServiceLink } from './ServiceLink';

interface MemberCardProps {
  blok: MemberCardStoryblok;
  variant: 'north_america' | 'others';
}

/**
 * Resolves the href from a Storyblok multilink field.
 */
const resolveLinkHref = (link: StoryblokMultilink | undefined): string | null => {
  if (!link) return null;
  const href = link.cached_url || link.url;
  return href || null;
};

/**
 * Extracts initials from a member name for fallback logo display.
 * - Single word or abbreviation: use as-is (e.g., "AANT" → "AANT")
 * - Multiple words: first letter of each word (e.g., "United States" → "US")
 */
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  // If single word or looks like an abbreviation, use first word
  if (words.length === 1 || /^[A-Z]{2,}$/.test(words[0])) {
    return words[0];
  }
  // Multiple words: take first letter of each (max 4)
  return words
    .slice(0, 4)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * MemberCard displays a single member organization.
 *
 * North America variant: Logo + Title, divider, list of service links
 * Others variant: Logo + clickable Title + Location (compact)
 */
export const MemberCard: React.FC<MemberCardProps> = ({ blok, variant }) => {
  const isNorthAmerica = variant === 'north_america';
  const externalUrl = resolveLinkHref(blok.external_url as StoryblokMultilink | undefined);
  const hasExternalUrl = Boolean(externalUrl);
  const logoSrc = blok.logo?.filename || undefined;
  const logoAlt = blok.logo?.alt ?? '';
  const serviceLinks = blok.service_links ?? [];

  // North America: fixed 282px width per Figma; Others: full width in grid
  const widthClass = isNorthAmerica ? 'w-full desktop:w-[282px]' : 'w-full';

  const cardClasses = [
    'bg-neutral-white',
    'rounded-[8px]',
    'border border-transparent',
    'shadow-[0px_4px_64px_0px_rgba(28,43,58,0.15)]',
    'p-4',
    'flex items-start',
    widthClass,
    'transition-all',
    'hover:bg-neutral-off-white',
    'hover:border-accent-pale-mist',
  ].join(' ');

  // Editable attributes for Storyblok Visual Editor
  const editableProps = storyblokEditable(blok as unknown as SbBlokData);

  // Title element - clickable for Others variant when URL exists
  const titleElement =
    !isNorthAmerica && hasExternalUrl ? (
      <a
        href={externalUrl ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="text-caption font-bold text-brand-deep hover:text-brand-blue hover:underline transition-colors break-words"
      >
        {blok.name}
      </a>
    ) : (
      <span className="text-caption font-bold text-brand-deep break-words">{blok.name}</span>
    );

  // Logo container classes based on whether logo exists
  const logoContainerClasses = logoSrc
    ? 'w-[90px] h-[77px] shrink-0 rounded-[8px] border border-gray-300 bg-white overflow-hidden flex items-center justify-center'
    : 'w-[90px] h-[77px] shrink-0 rounded-[8px] border border-gray-300 bg-[#f4f6f8] flex items-center justify-center';

  // Initials for fallback display
  const initials = getInitials(blok.name);

  if (isNorthAmerica) {
    // North America layout: Logo + Title (top), divider, service links (bottom)
    return (
      <article {...editableProps} className={cardClasses}>
        <div className="flex flex-col gap-4 w-full">
          {/* Top section: Logo + Title */}
          <div className="flex items-center gap-4">
            <div className={logoContainerClasses}>
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  className="max-w-full max-h-full object-contain p-1"
                />
              ) : (
                <span className="text-lg font-bold text-[#1c2b3a]">{initials}</span>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-center">{titleElement}</div>
          </div>

          {/* Divider */}
          {serviceLinks.length > 0 && (
            <div className="w-full h-px bg-gray-300" aria-hidden="true" />
          )}

          {/* Service links */}
          {serviceLinks.length > 0 && (
            <div className="flex flex-col gap-4">
              {serviceLinks.map((link) => (
                <ServiceLink key={link._uid} blok={link} />
              ))}
            </div>
          )}
        </div>
      </article>
    );
  }

  // Others layout: Logo + Title + Location (compact, no divider)
  return (
    <article {...editableProps} className={cardClasses}>
      <div className="flex items-start gap-3 w-full">
        {/* Logo */}
        <div className={logoContainerClasses}>
          {logoSrc ? (
            <img src={logoSrc} alt={logoAlt} className="max-w-full max-h-full object-contain p-1" />
          ) : (
            <span className="text-lg font-bold text-[#1c2b3a]">{initials}</span>
          )}
        </div>

        {/* Title + Location */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {titleElement}
          {blok.location && (
            <span className="text-callout text-gray-700 break-words">{blok.location}</span>
          )}
        </div>
      </div>
    </article>
  );
};
